import api from "./axios";

// =========================================================
// LIST EMPLOYEES
// =========================================================

export const getEmployees = (
  page = 1,
  limit = 10,
  search = "",
  filters = {}
) => {
  const params = new URLSearchParams();

  params.set(
    "page",
    String(page)
  );

  params.set(
    "limit",
    String(limit)
  );

  // -------------------------------------------------------
  // SEARCH
  // -------------------------------------------------------

  if (search) {
    params.set(
      "search",
      search
    );
  }

  // -------------------------------------------------------
  // EMPLOYEE ID
  // -------------------------------------------------------

  if (filters?.employeeId) {
    params.set(
      "employeeId",
      String(
        filters.employeeId
      )
    );
  }

  // -------------------------------------------------------
  // STATUS
  // -------------------------------------------------------

  if (filters?.status) {
    params.set(
      "status",
      filters.status
    );
  }

  // -------------------------------------------------------
  // JOINING DATE FROM
  // -------------------------------------------------------

  if (filters?.joiningDateFrom) {
    params.set(
      "joiningDateFrom",
      filters.joiningDateFrom
    );
  }

  // -------------------------------------------------------
  // JOINING DATE TO
  // -------------------------------------------------------

  if (filters?.joiningDateTo) {
    params.set(
      "joiningDateTo",
      filters.joiningDateTo
    );
  }

  // -------------------------------------------------------
  // ROLE
  // -------------------------------------------------------

  if (filters?.roleSlug) {
    params.set(
      "roleSlug",
      filters.roleSlug
    );
  }

  return api.get(
    `/employees?${params.toString()}`
  );
};


// =========================================================
// GET SINGLE EMPLOYEE
// =========================================================

export const getEmployee = (
  id
) => {
  return api.get(
    `/employees/${id}`
  );
};


// =========================================================
// CREATE EMPLOYEE
// =========================================================
//
// The complete employee payload is passed directly.
// This supports:
//
// - Basic employee details
// - Organization/location
// - Permanent address
// - Correspondence address
// - TA/DA policy
// - Territory mapping
// - Password
// - Status
//
// Existing flow preserved.
// =========================================================

export const createEmployee = (
  data
) => {
  return api.post(
    "/employees",
    data
  );
};


// =========================================================
// UPDATE EMPLOYEE
// =========================================================
//
// Same generic payload approach as create.
// Existing update flow preserved.
// =========================================================

export const updateEmployee = (
  id,
  data
) => {
  return api.patch(
    `/employees/${id}`,
    data
  );
};


// =========================================================
// UPDATE EMPLOYEE STATUS
// =========================================================

export const updateEmployeeStatus = (
  id,
  status
) => {
  return api.patch(
    `/employees/${id}/status`,
    {
      status,
    }
  );
};


// =========================================================
// RESET EMPLOYEE PASSWORD
// =========================================================

export const resetEmployeePassword = (
  id,
  password
) => {
  return api.post(
    `/employees/${id}/reset-password`,
    {
      password,
    }
  );
};


// =========================================================
// DELETE EMPLOYEE
// =========================================================
//
// Kept as existing API function.
// If backend delete endpoint is added later,
// no frontend import changes will be required.
// =========================================================

export const deleteEmployee = (
  id
) => {
  return api.delete(
    `/employees/${id}`
  );
};


// =========================================================
// EMPLOYEE DOCUMENTS
// =========================================================

// ---------------------------------------------------------
// GET DOCUMENTS
// ---------------------------------------------------------

export const getEmployeeDocuments = (
  employeeId
) => {
  return api.get(
    `/employees/${employeeId}/documents`
  );
};


// ---------------------------------------------------------
// UPLOAD DOCUMENT
// ---------------------------------------------------------

export const uploadEmployeeDocument = (
  employeeId,
  {
    file,
    documentType,
    documentNumber = "",
    remarks = "",
  }
) => {
  const formData =
    new FormData();

  formData.append(
    "file",
    file
  );

  formData.append(
    "documentType",
    documentType
  );

  if (documentNumber) {
    formData.append(
      "documentNumber",
      documentNumber
    );
  }

  if (remarks) {
    formData.append(
      "remarks",
      remarks
    );
  }

  return api.post(
    `/employees/${employeeId}/documents`,
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data",
      },
    }
  );
};


// ---------------------------------------------------------
// GET DOCUMENT URL
// ---------------------------------------------------------

export const getEmployeeDocumentUrl = (
  fileUrl
) => {
  if (!fileUrl) {
    return "";
  }

  // Already absolute URL
  if (
    /^https?:\/\//i.test(
      fileUrl
    )
  ) {
    return fileUrl;
  }

  const backendUrl =
    process.env.REACT_APP_API_URL;

  if (!backendUrl) {
    throw new Error(
      "REACT_APP_API_URL is not configured."
    );
  }

  return new URL(
    fileUrl.startsWith("/")
      ? fileUrl
      : `/${fileUrl}`,
    backendUrl
  ).toString();
};


// ---------------------------------------------------------
// DELETE DOCUMENT
// ---------------------------------------------------------

export const deleteEmployeeDocument = (
  employeeId,
  documentId
) => {
  return api.delete(
    `/employees/${employeeId}/documents/${documentId}`
  );
};


// =========================================================
// IMPORT EMPLOYEES
// =========================================================
//
// Supports backend:
//
// POST /employees/import
//
// File types:
// - CSV
// - XLS
// - XLSX
//
// Backend limit:
// 10 MB
//
// Existing import flow preserved.
// =========================================================

export const importEmployees = (
  file
) => {
  if (!file) {
    return Promise.reject(
      new Error(
        "Please select a CSV or Excel file."
      )
    );
  }

  const allowedExtensions = [
    ".csv",
    ".xls",
    ".xlsx",
  ];

  const fileName =
    String(
      file.name || ""
    ).toLowerCase();

  const extension =
    fileName.includes(".")
      ? fileName.substring(
          fileName.lastIndexOf(".")
        )
      : "";

  if (
    !allowedExtensions.includes(
      extension
    )
  ) {
    return Promise.reject(
      new Error(
        "Only CSV, XLS and XLSX files are supported."
      )
    );
  }

  // Backend allows maximum 10 MB.
  if (
    file.size >
    10 * 1024 * 1024
  ) {
    return Promise.reject(
      new Error(
        "File size must be 10 MB or less."
      )
    );
  }

  const formData =
    new FormData();

  formData.append(
    "file",
    file
  );

  return api.post(
    "/employees/import",
    formData,
    {
      headers: {
        "Content-Type":
          "multipart/form-data",
      },
    }
  );
};